import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, Check, X, Mail, UploadCloud } from "lucide-react";

const TABS = [
  { key: "testimonials", label: "Testimonials" },
  { key: "gallery", label: "Gallery" },
  { key: "blog", label: "Blog" },
  { key: "inquiries", label: "Inquiries" },
];

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(!!location.state?.user);
  const [tab, setTab] = useState("testimonials");

  useEffect(() => {
    if (location.state?.user) {
      setAuthChecked(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        if (!r.data?.is_admin) {
          navigate("/admin/login", { replace: true });
          return;
        }
        setUser(r.data);
        setAuthChecked(true);
      })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [location.state, navigate]);

  const onLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    navigate("/admin/login", { replace: true });
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory">
        <p className="text-ink/60">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory">
      <header className="border-b border-ink-10 bg-ivory/95 backdrop-blur-xl sticky top-0 z-30" style={{ borderColor: "rgba(11,31,56,0.12)" }}>
        <div className="container-prose flex items-center justify-between h-16">
          <div className="flex items-center gap-6">
            <Logo />
            <span className="hidden sm:inline overline">Admin Console</span>
          </div>
          <div className="flex items-center gap-4">
            {user?.email && (
              <span className="text-sm text-ink/70 hidden sm:inline">{user.email}</span>
            )}
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] border border-ink/30 px-4 py-2 hover:bg-ink hover:text-ivory transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-prose py-10">
        <div className="flex flex-wrap gap-2 mb-8 border-b border-ink-10" style={{ borderColor: "rgba(11,31,56,0.18)" }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-[12px] uppercase tracking-[0.22em] -mb-px border-b-2 ${
                tab === t.key
                  ? "border-forest text-ink"
                  : "border-transparent text-ink/50 hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "testimonials" && <TestimonialsAdmin />}
        {tab === "gallery" && <GalleryAdmin />}
        {tab === "blog" && <BlogAdmin />}
        {tab === "inquiries" && <InquiriesAdmin />}
      </main>
    </div>
  );
}

/* ---------- Testimonials ---------- */
function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", role: "", organization: "", quote: "", avatar_url: "", published: true };
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const r = await api.get("/admin/testimonials");
      setItems(r.data || []);
    } catch (e) { toast.error("Could not load testimonials"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.quote) { toast.error("Name and quote are required"); return; }
    try {
      if (editing) await api.put(`/admin/testimonials/${editing}`, form);
      else await api.post("/admin/testimonials", form);
      toast.success("Saved");
      setForm(empty); setEditing(null); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5">
        <h3 className="text-2xl text-ink mb-4" style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}>
          {editing ? "Edit Testimonial" : "Add Testimonial"}
        </h3>
        <div className="space-y-3">
          <Input label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="t-name" />
          <Input label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="t-role" />
          <Input label="Organisation" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="t-org" />
          <ImageUpload label="Upload avatar" value={form.avatar_url} onChange={(v) => setForm({ ...form, avatar_url: v })} testid="t-upload" />
          <Input label="Avatar URL" value={form.avatar_url} onChange={(v) => setForm({ ...form, avatar_url: v })} testid="t-avatar" />
          <Textarea label="Quote *" value={form.quote} onChange={(v) => setForm({ ...form, quote: v })} testid="t-quote" />
          <label className="flex items-center gap-2 text-sm text-ink/80">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="t-published" />
            Published
          </label>
          <div className="flex gap-3">
            <button onClick={save} data-testid="t-save" className="px-5 py-2.5 bg-ink text-ivory text-[12px] uppercase tracking-[0.22em]">
              {editing ? "Update" : "Add"}
            </button>
            {editing && (
              <button onClick={() => { setEditing(null); setForm(empty); }} className="px-5 py-2.5 border border-ink/30 text-[12px] uppercase tracking-[0.22em]">
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="lg:col-span-7 space-y-3">
        {items.length === 0 && <p className="text-ink/50">No testimonials yet.</p>}
        {items.map((t) => (
          <div key={t.id} data-testid={`t-row-${t.id}`} className="border border-ink-10 p-4 bg-white" style={{ borderColor: "rgba(11,31,56,0.18)" }}>
            <div className="flex justify-between items-start gap-4">
              <div>
                <p className="text-sm font-medium text-ink">{t.name} <span className="text-ink/50 font-normal">· {t.role || "—"}</span></p>
                <p className="mt-1 italic text-ink/80 text-sm" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                  "{t.quote}"
                </p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-forest">{t.published ? "Published" : "Draft"}</p>
              </div>
              <div className="flex gap-2">
                <IconBtn onClick={() => { setEditing(t.id); setForm({ name: t.name, role: t.role || "", organization: t.organization || "", quote: t.quote, avatar_url: t.avatar_url || "", published: !!t.published }); }} icon={<Pencil size={14} />} />
                <IconBtn onClick={() => del(t.id)} icon={<Trash2 size={14} />} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Gallery ---------- */
function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const empty = { image_url: "", caption: "", location: "", event_date: "", order: 0 };
  const [form, setForm] = useState(empty);

  const load = async () => { const r = await api.get("/admin/gallery"); setItems(r.data || []); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.image_url) { toast.error("Image URL required"); return; }
    try { await api.post("/admin/gallery", { ...form, order: parseInt(form.order) || 0 }); setForm(empty); load(); toast.success("Added"); }
    catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/gallery/${id}`); load(); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5 space-y-3">
        <h3 className="text-2xl text-ink mb-4" style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}>Add Image</h3>
        <ImageUpload label="Upload image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="g-upload" />
        <Input label="Image URL *" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="g-url" />
        <Input label="Caption" value={form.caption} onChange={(v) => setForm({ ...form, caption: v })} testid="g-caption" />
        <Input label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="g-loc" />
        <Input label="Event date" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} testid="g-date" />
        <Input label="Order" value={form.order} onChange={(v) => setForm({ ...form, order: v })} testid="g-order" />
        <button onClick={add} data-testid="g-save" className="px-5 py-2.5 bg-ink text-ivory text-[12px] uppercase tracking-[0.22em]">Add</button>
      </div>
      <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map((g) => (
          <div key={g.id} data-testid={`g-row-${g.id}`} className="relative group border border-ink-10" style={{ borderColor: "rgba(11,31,56,0.18)" }}>
            <img src={mediaUrl(g.image_url)} alt={g.caption || ""} className="w-full aspect-square object-cover" />
            <button onClick={() => del(g.id)} className="absolute top-2 right-2 bg-ink text-ivory p-2 opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
            {g.caption && <p className="p-2 text-xs text-ink/70">{g.caption}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Blog ---------- */
function BlogAdmin() {
  const [items, setItems] = useState([]);
  const empty = { title: "", excerpt: "", content: "", cover_image: "", tags: [], published: true };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => { const r = await api.get("/admin/blog"); setItems(r.data || []); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.content) { toast.error("Title and content required"); return; }
    try {
      if (editing) await api.put(`/admin/blog/${editing}`, form);
      else await api.post("/admin/blog", form);
      setForm(empty); setEditing(null); load(); toast.success("Saved");
    } catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/blog/${id}`); load(); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5 space-y-3">
        <h3 className="text-2xl text-ink mb-4" style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}>{editing ? "Edit Post" : "New Post"}</h3>
        <Input label="Title *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="b-title" />
        <Input label="Excerpt" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} testid="b-excerpt" />
        <ImageUpload label="Upload cover image" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testid="b-upload" />
        <Input label="Cover image URL" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testid="b-cover" />
        <Input label="Tags (comma-separated)" value={form.tags.join(", ")} onChange={(v) => setForm({ ...form, tags: v.split(",").map((s) => s.trim()).filter(Boolean) })} testid="b-tags" />
        <Textarea label="Content (markdown / plain text) *" rows={10} value={form.content} onChange={(v) => setForm({ ...form, content: v })} testid="b-content" />
        <label className="flex items-center gap-2 text-sm text-ink/80">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="b-published" />
          Published
        </label>
        <div className="flex gap-3">
          <button onClick={save} data-testid="b-save" className="px-5 py-2.5 bg-ink text-ivory text-[12px] uppercase tracking-[0.22em]">{editing ? "Update" : "Publish"}</button>
          {editing && <button onClick={() => { setEditing(null); setForm(empty); }} className="px-5 py-2.5 border border-ink/30 text-[12px] uppercase tracking-[0.22em]">Cancel</button>}
        </div>
      </div>
      <div className="lg:col-span-7 space-y-3">
        {items.map((p) => (
          <div key={p.id} data-testid={`b-row-${p.id}`} className="border border-ink-10 p-4 bg-white flex justify-between items-start gap-4" style={{ borderColor: "rgba(11,31,56,0.18)" }}>
            <div>
              <p className="text-base text-ink" style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}>{p.title}</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-forest mt-1">{p.published ? "Published" : "Draft"}</p>
              <p className="text-xs text-ink/55 mt-1">/{p.slug}</p>
            </div>
            <div className="flex gap-2">
              <IconBtn onClick={() => { setEditing(p.id); setForm({ title: p.title, excerpt: p.excerpt || "", content: p.content, cover_image: p.cover_image || "", tags: p.tags || [], published: !!p.published }); }} icon={<Pencil size={14} />} />
              <IconBtn onClick={() => del(p.id)} icon={<Trash2 size={14} />} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Inquiries ---------- */
function InquiriesAdmin() {
  const [items, setItems] = useState([]);
  const load = async () => { const r = await api.get("/admin/inquiries"); setItems(r.data || []); };
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.put(`/admin/inquiries/${id}/read`); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/inquiries/${id}`); load(); };

  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-ink/50">No inquiries yet.</p>}
      {items.map((i) => (
        <div key={i.id} data-testid={`i-row-${i.id}`} className={`border p-5 ${i.read ? "bg-white border-ink-10" : "bg-ivorySoft border-forest/40"}`} style={{ borderColor: i.read ? "rgba(11,31,56,0.18)" : "rgba(27,77,62,0.4)" }}>
          <div className="flex justify-between items-start gap-4">
            <div>
              <div className="flex items-center gap-3">
                <p className="text-base font-medium text-ink">{i.name}</p>
                {!i.read && <span className="text-[10px] uppercase tracking-[0.22em] bg-forest text-ivory px-2 py-0.5">New</span>}
              </div>
              <p className="text-xs text-ink/60">{i.email} · {i.organization || "—"} · {i.inquiry_type}</p>
              <p className="mt-3 text-sm text-ink/80 whitespace-pre-wrap">{i.message}</p>
              <p className="text-[11px] text-ink/50 mt-2">{new Date(i.created_at).toLocaleString()}</p>
            </div>
            <div className="flex flex-col gap-2">
              {!i.read && <IconBtn onClick={() => markRead(i.id)} icon={<Check size={14} />} />}
              <a href={`mailto:${i.email}`} className="inline-flex items-center justify-center h-9 w-9 border border-ink/30 hover:bg-ink hover:text-ivory transition-colors"><Mail size={14} /></a>
              <IconBtn onClick={() => del(i.id)} icon={<Trash2 size={14} />} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- shared ---------- */
function Input({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.22em] text-ink/60">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full mt-1 px-3 py-2 border border-ink-10 bg-white text-ink text-sm focus:outline-none focus:border-forest" style={{ borderColor: "rgba(11,31,56,0.22)" }} />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 4, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.22em] text-ink/60">{label}</span>
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full mt-1 px-3 py-2 border border-ink-10 bg-white text-ink text-sm focus:outline-none focus:border-forest resize-y" style={{ borderColor: "rgba(11,31,56,0.22)" }} />
    </label>
  );
}
function IconBtn({ onClick, icon }) {
  return (
    <button onClick={onClick} className="inline-flex items-center justify-center h-9 w-9 border border-ink/25 hover:bg-ink hover:text-ivory transition-colors">
      {icon}
    </button>
  );
}

function ImageUpload({ label, value, onChange, testid }) {
  const [progress, setProgress] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPG, PNG or WEBP images are allowed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be 10 MB or smaller");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      const r = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) =>
          setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 50),
      });
      onChange(r.data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="space-y-2">
      <span className="text-[11px] uppercase tracking-[0.22em] text-ink/60">{label}</span>
      <div className="flex items-start gap-3">
        <label
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-dashed border-ink/35 text-[12px] uppercase tracking-[0.18em] cursor-pointer hover:bg-ink hover:text-ivory transition-colors"
          data-testid={`${testid}-label`}
        >
          <UploadCloud size={15} />
          {progress !== null ? `Uploading ${progress}%` : "Choose image"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            data-testid={testid}
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </label>
        {value && (
          <div className="relative">
            <img
              src={mediaUrl(value)}
              alt="preview"
              data-testid={`${testid}-preview`}
              className="h-16 w-16 object-cover border border-ink/15"
            />
            <button
              onClick={() => onChange("")}
              data-testid={`${testid}-clear`}
              className="absolute -top-2 -right-2 h-6 w-6 grid place-items-center bg-ink text-ivory"
            >
              <X size={12} />
            </button>
          </div>
        )}
      </div>
      {progress !== null && (
        <div className="h-1 bg-ink/10">
          <div className="h-full bg-forest transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
      <p className="text-[11px] text-ink/45">JPG, PNG or WEBP · max 10 MB. You can also paste a URL below.</p>
    </div>
  );
}
